/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ApiHolder } from '@backstage/core-plugin-api';
import { JsonObject, JsonValue } from '@backstage/types';
import { FormValidation } from '@rjsf/utils';
import { CustomFieldValidator } from '../../extensions/types';

export function isObject(obj: unknown): obj is JsonObject {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

/**
 * Collects properties from allOf conditional branches (if/then/else).
 * This allows validators to locate field schemas that are not in the root
 * `properties` object but are instead defined inside allOf conditions.
 */
function collectPropertiesFromAllOf(schema: JsonObject): JsonObject {
  const collected: JsonObject = {};

  if (!Array.isArray(schema.allOf)) return collected;

  for (const entry of schema.allOf) {
    if (!isObject(entry)) continue;

    if (entry.if) {
      // Conditional entry: collect from then/else branches
      if (isObject(entry.then)) {
        const thenProps = (entry.then as JsonObject).properties;
        if (isObject(thenProps)) {
          Object.assign(collected, thenProps);
        }
        // Recurse into nested allOf inside then
        Object.assign(
          collected,
          collectPropertiesFromAllOf(entry.then as JsonObject),
        );
      }
      if (isObject(entry.else)) {
        const elseProps = (entry.else as JsonObject).properties;
        if (isObject(elseProps)) {
          Object.assign(collected, elseProps);
        }
        // Recurse into nested allOf inside else
        Object.assign(
          collected,
          collectPropertiesFromAllOf(entry.else as JsonObject),
        );
      }
    } else {
      // Non-conditional allOf entry: collect its properties directly
      if (isObject(entry.properties)) {
        Object.assign(collected, entry.properties);
      }
      // Recurse into nested allOf
      Object.assign(collected, collectPropertiesFromAllOf(entry));
    }
  }

  return collected;
}

export const createValidator = (
  rootSchema: JsonObject,
  validators: Record<string, undefined | CustomFieldValidator<unknown>>,
  context: {
    apiHolder: ApiHolder;
  },
): ((formData: JsonObject, errors: FormValidation) => FormValidation) => {
  function validate(
    schema: JsonObject,
    formData: JsonObject,
    errors: FormValidation,
  ) {
    const schemaProps = schema.properties;
    const customObject = schema.type === 'object' && schemaProps === undefined;

    if (!isObject(schemaProps) && !customObject) {
      return;
    }

    if (schemaProps) {
      // Merge properties from allOf conditional branches so that fields defined
      // inside allOf -> if/then/else are also validated by custom field validators.
      const effectiveSchemaProps: JsonObject = {
        ...schemaProps,
        ...collectPropertiesFromAllOf(schema),
      };

      for (const [key, propData] of Object.entries(formData)) {
        const propValidation = errors[key] as FormValidation;

        if (isObject(propData)) {
          const propSchemaProps = effectiveSchemaProps[key];
          if (isObject(propSchemaProps)) {
            validate(
              propSchemaProps,
              propData,
              propValidation as FormValidation,
            );
          }
        } else if (Array.isArray(propData)) {
          const schemaKeyProps = effectiveSchemaProps[key] as
            | JsonObject
            | undefined;

          // Call the field validator for array-type custom fields
          const arrayFieldName =
            isObject(schemaKeyProps) && (schemaKeyProps['ui:field'] as string);
          if (
            arrayFieldName &&
            typeof validators[arrayFieldName] === 'function'
          ) {
            const isRequired =
              Array.isArray(schema.required) &&
              (schema.required as string[]).includes(key);
            validators[arrayFieldName]!(
              propData,
              propValidation,
              context,
              { ...schemaKeyProps, _isRequired: isRequired },
              formData,
              key,
            );
          }

          const items = schemaKeyProps?.items as JsonObject | undefined;

          propData?.forEach((value: any, index: number) => {
            if (typeof value === 'object' && value !== null) {
              Object.keys(value as Record<string, unknown>).forEach(
                valueKey => {
                  if (items?.properties && isObject(items.properties)) {
                    const fieldValue = (items.properties as JsonObject)[
                      valueKey
                    ] as JsonObject | undefined;
                    const fieldValueName = fieldValue?.['ui:field'] as
                      | string
                      | undefined;

                    if (
                      fieldValueName &&
                      typeof validators[fieldValueName] === 'function'
                    ) {
                      validators[fieldValueName]!(
                        value[valueKey] as JsonValue,
                        propValidation[index] as FormValidation,
                        context,
                        { validation: fieldValue?.validation },
                        formData,
                        valueKey,
                      );
                    }
                  }
                },
              );
            }

            if (items && isObject(items)) {
              const fieldValue = items as JsonObject;
              const fieldValueName = fieldValue?.['ui:field'] as
                | string
                | undefined;

              if (
                fieldValueName &&
                typeof validators[fieldValueName] === 'function'
              ) {
                validators[fieldValueName]!(
                  value,
                  propValidation,
                  context,
                  { validation: fieldValue?.validation },
                  formData,
                  index.toString(),
                );
              }
            }
          });
        } else {
          const propSchema = effectiveSchemaProps[key];
          const fieldName =
            isObject(propSchema) && (propSchema['ui:field'] as string);

          if (fieldName && typeof validators[fieldName] === 'function') {
            validators[fieldName]!(
              propData as JsonValue,
              propValidation,
              context,
              propSchema,
              formData,
            );
          }
        }
      }
    } else if (customObject) {
      const fieldName = schema['ui:field'] as string;
      if (fieldName && typeof validators[fieldName] === 'function') {
        validators[fieldName]!(formData, errors, context, null, null);
      }
    }
  }

  return (formData: JsonObject, errors: FormValidation): FormValidation => {
    validate(rootSchema, formData, errors);
    return errors;
  };
};
